import React from 'react';
import { 
  Brain, BookOpen, BrainCircuit, PieChart as PieChartIcon, TrendingDown,
  Activity, Zap, Compass, Users, Heart, User, Briefcase, Target,
  List, Star, Download, Loader2, BarChart3, Sparkles 
} from 'lucide-react';
import { 
  ResponsiveContainer, RadarChart, PolarGrid, 
  PolarAngleAxis, PolarRadiusAxis, Radar, Tooltip 
} from 'recharts';

import EditableAIBox from '../EditableAIBox';

// --- 1. BİREYSEL RAPOR ARAYÜZÜ ---
export const CokluZekaReportUI = ({ data, meta }) => {
  const chartData = data.chartData || [];
  const top1 = data.topIntelligences?.[0] || { subject: "Belirsiz", A: 0 };
  const top2 = data.topIntelligences?.[1] || { subject: "Belirsiz", A: 0 };

  const DICT = {
    "Sözel-Dilsel Zeka": {
      icon: BookOpen, color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200",
      learnsBy: "Okuyarak, yazarak, dinleyerek ve konuşarak öğrenir.",
      traits: "Kelimelerle arası çok iyidir. Hikaye anlatmayı, tartışmayı, espri yapmayı ve kelime oyunlarını sever. Hafızası kuvvetlidir.",
      careers: "Yazar, Şair, Gazeteci, Politikacı, Avukat, Çevirmen, Edebiyat Öğretmeni"
    },
    "Mantıksal-Matematiksel Zeka": {
      icon: BrainCircuit, color: "text-indigo-700", bg: "bg-indigo-50", border: "border-indigo-200",
      learnsBy: "Neden-sonuç ilişkisi kurarak, sınıflandırarak ve soyut modellerle öğrenir.",
      traits: "Sayılarla arası iyidir. Problem çözmeyi, deney yapmayı, satranç gibi strateji oyunlarını ve sorgulamayı sever.",
      careers: "Bilim İnsanı, Mühendis, Bilgisayar Programcısı, Muhasebeci, Matematikçi, Ekonomist"
    },
    "Görsel-Uzamsal Zeka": {
      icon: PieChartIcon, color: "text-purple-700", bg: "bg-purple-50", border: "border-purple-200",
      learnsBy: "Görselleştirerek, resimlerle, haritalarla ve renklerle öğrenir.",
      traits: "Yön bulma duygusu gelişmiştir. Resim yapmayı, maket tasarlamayı ve hayal kurmayı sever. Görsel hafızası güçlüdür.",
      careers: "Mimar, Ressam, Heykeltıraş, Tasarımcı, Haritacı, Pilot, Cerrah, Fotoğrafçı"
    },
    "Müziksel-Ritmik Zeka": {
      icon: Activity, color: "text-rose-700", bg: "bg-rose-50", border: "border-rose-200",
      learnsBy: "Ritim tutarak, mırıldanarak ve müziğe tempo uydurarak öğrenir.",
      traits: "Seslere karşı çok duyarlıdır. Melodileri kolay hatırlar, enstrüman çalmaya yatkındır.",
      careers: "Müzisyen, Besteci, Şarkıcı, Müzik Öğretmeni, Ses Teknisyeni"
    },
    "Bedensel-Kinestetik Zeka": {
      icon: Zap, color: "text-orange-700", bg: "bg-orange-50", border: "border-orange-200",
      learnsBy: "Dokunarak, hareket ederek, deneyimleyerek ve yaparak öğrenir.",
      traits: "Uzun süre sabit durmakta zorlanır. El becerileri gelişmiştir, jest ve mimiklerini çok kullanır. Spor ve dansa yatkındır.",
      careers: "Sporcu, Dansçı, Oyuncu, Cerrah, Heykeltıraş, Koreograf, Tamirci"
    },
    "Doğacı Zeka": {
      icon: Compass, color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200",
      learnsBy: "Doğayı gözlemleyerek, inceleyerek ve sınıflandırarak öğrenir.",
      traits: "Bitkilere, hayvanlara ve doğa olaylarına karşı yoğun ilgi duyar. Açık havada olmaktan keyif alır.",
      careers: "Biyolog, Veteriner, Ziraat Mühendisi, Çevre Uzmanı, Botanikçi, Belgeselci"
    },
    "Sosyal (Kişiler Arası) Zeka": {
      icon: Users, color: "text-cyan-700", bg: "bg-cyan-50", border: "border-cyan-200",
      learnsBy: "Grupla çalışarak, paylaşarak, tartışarak ve işbirliği yaparak öğrenir.",
      traits: "Liderlik vasfı yüksektir. Empati kurar, ikna kabiliyeti yüksektir ve çevresinde çok arkadaşı vardır.",
      careers: "Öğretmen, Psikolog, Danışman, Pazarlamacı, Halkla İlişkiler, Yönetici"
    },
    "İçsel (Öze Dönük) Zeka": {
      icon: Heart, color: "text-slate-700", bg: "bg-slate-50", border: "border-slate-200",
      learnsBy: "Kendi başına çalışarak, derin düşünerek ve kendi hızında öğrenir.",
      traits: "Kendi duygu ve düşüncelerinin farkındadır. Bağımsızdır, özgüveni yüksektir, günlük tutmayı ve yalnız kalmayı sever.",
      careers: "Yazar, Psikoterapist, Filozof, Din Adamı, Araştırmacı, Sanatçı"
    }
  };

  const p1 = DICT[top1.subject] || DICT["Sözel-Dilsel Zeka"];
  const p2 = DICT[top2.subject] || DICT["Mantıksal-Matematiksel Zeka"];

  return (
    <div className="space-y-6 print:space-y-4 font-sans flex flex-col print:min-h-[260mm]">
      {/* ÜST BAŞLIK */}
      <div className="bg-slate-900 text-white p-4 print:p-3 rounded-xl flex justify-between items-center shadow-sm print:bg-white print:text-black print:shadow-none print:border-b-2 print:border-slate-900">
        <div className="flex items-center gap-4 print:gap-3">
          <div className="h-12 w-12 print:h-9 print:w-9 rounded-xl flex items-center justify-center shadow-inner bg-indigo-600 print:bg-slate-800">
            <Brain className="h-6 w-6 print:h-4 print:w-4 text-white"/>
          </div>
          <div>
            <h2 className="text-xl print:text-lg font-bold leading-none mb-1">Çoklu Zeka Profili</h2>
            <span className="text-[10px] print:text-[10px] text-slate-300 print:text-slate-600 font-bold tracking-widest uppercase block">
               {meta?.studentClass || "Sınıf Belirtilmemiş"} • ÖĞRENCİ NO: {meta?.studentNo || "-"}
            </span>
          </div>
        </div>
        <div className="bg-white/10 px-4 py-2 print:px-3 print:py-1.5 rounded-lg text-center print:border print:border-slate-300">
          <span className="text-2xl print:text-xl font-black block leading-none text-indigo-300 print:text-indigo-700">{top1.A}</span>
          <span className="text-[9px] print:text-[9px] block opacity-70 uppercase font-bold mt-0.5">Max Puan</span>
        </div>
      </div>

      {/* RADAR GRAFİK VE BASKIN TİPLER */}
      <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-4 print:gap-4 shrink-0 avoid-break">
        
        {/* RADAR CHART */}
        <div className="bg-white p-2 rounded-2xl border border-slate-200 shadow-sm flex flex-col items-center justify-center h-[300px] print:h-[250px] print:border-slate-300">
          <h3 className="font-bold text-slate-800 text-xs uppercase tracking-widest mt-2">Zeka Ağı Dağılımı</h3>
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart cx="50%" cy="50%" outerRadius="70%" data={chartData}>
              <PolarGrid stroke="#e2e8f0" />
              <PolarAngleAxis dataKey="subject" tick={{ fill: '#64748b', fontSize: 9, fontWeight: 'bold' }} />
              <PolarRadiusAxis angle={30} domain={[0, 40]} tick={false} axisLine={false} />
              <Radar name="Öğrenci" dataKey="A" stroke="#4f46e5" strokeWidth={2} fill="#6366f1" fillOpacity={0.5} />
              <Tooltip formatter={(value) => [`${value} Puan`, 'Skor']} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* BASKIN 2 ZEKA KARTI */}
        <div className="flex flex-col gap-4 print:gap-3">
          <div className={`${p1.bg} p-5 print:p-4 rounded-2xl border ${p1.border} shadow-sm flex-1 flex flex-col justify-center print:bg-white print:border-slate-300`}>
            <div className="flex items-center mb-2 border-b pb-2 opacity-80 print:border-slate-200">
              <p1.icon className={`h-5 w-5 print:h-4 print:w-4 mr-2 ${p1.color}`}/>
              <span className={`text-xs font-black uppercase tracking-widest ${p1.color}`}>1. Baskın Zeka Alanı</span>
            </div>
            <h3 className={`text-xl print:text-lg font-black leading-tight mb-2 ${p1.color}`}>{top1.subject}</h3>
            <p className="text-[11px] print:text-[9.5px] text-slate-700 font-medium leading-snug"><strong>Öğrenme Stili:</strong> {p1.learnsBy}</p>
          </div>

          <div className={`${p2.bg} p-5 print:p-4 rounded-2xl border ${p2.border} shadow-sm flex-1 flex flex-col justify-center print:bg-white print:border-slate-300`}>
            <div className="flex items-center mb-2 border-b pb-2 opacity-80 print:border-slate-200">
              <p2.icon className={`h-5 w-5 print:h-4 print:w-4 mr-2 ${p2.color}`}/>
              <span className={`text-xs font-black uppercase tracking-widest ${p2.color}`}>2. Baskın Zeka Alanı</span>
            </div>
            <h3 className={`text-xl print:text-lg font-black leading-tight mb-2 ${p2.color}`}>{top2.subject}</h3>
            <p className="text-[11px] print:text-[9.5px] text-slate-700 font-medium leading-snug"><strong>Öğrenme Stili:</strong> {p2.learnsBy}</p>
          </div>
        </div>
      </div>

      {/* KARİYER VE ÖZELLİK VİTRİNİ */}
      <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-4 print:gap-4 shrink-0 avoid-break">
        <div className="bg-slate-50 p-5 print:p-4 rounded-2xl border border-slate-200 shadow-sm print:bg-white print:border-slate-300 flex flex-col">
           <h4 className="font-bold text-slate-800 text-xs uppercase tracking-widest border-b pb-2 mb-3 flex items-center"><User className="h-4 w-4 mr-2 text-slate-500"/> Temel Karakteristik Özellikler</h4>
           <p className="text-xs print:text-[10.5px] text-slate-700 font-medium leading-relaxed text-justify mb-3">
             <span className="font-bold text-indigo-700">{top1.subject}:</span> {p1.traits}
           </p>
           <p className="text-xs print:text-[10.5px] text-slate-700 font-medium leading-relaxed text-justify">
             <span className="font-bold text-indigo-700">{top2.subject}:</span> {p2.traits}
           </p>
        </div>
        <div className="bg-slate-50 p-5 print:p-4 rounded-2xl border border-slate-200 shadow-sm print:bg-white print:border-slate-300 flex flex-col">
           <h4 className="font-bold text-slate-800 text-xs uppercase tracking-widest border-b pb-2 mb-3 flex items-center"><Briefcase className="h-4 w-4 mr-2 text-slate-500"/> İlgili Kariyer ve Meslek Alanları</h4>
           <p className="text-xs print:text-[10.5px] text-slate-700 font-medium leading-relaxed text-justify mb-3">
             <span className="font-bold text-indigo-700">{top1.subject}:</span> {p1.careers}
           </p>
           <p className="text-xs print:text-[10.5px] text-slate-700 font-medium leading-relaxed text-justify">
             <span className="font-bold text-indigo-700">{top2.subject}:</span> {p2.careers}
           </p>
        </div>
      </div>

      {/* TÜM LİSTE (BARLAR) */}
      <div className="bg-white p-5 print:p-4 rounded-2xl border border-slate-200 shadow-sm flex-1 mt-2 avoid-break">
        <h3 className="font-bold text-slate-800 mb-4 print:mb-3 flex items-center text-sm print:text-xs border-b pb-3 print:pb-2 uppercase tracking-wide">
          <List className="h-4 w-4 mr-2 text-slate-500"/> Zeka Profilinin Genel Dağılımı
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-x-8 gap-y-4 print:gap-y-3">
          {data.details && data.details.map((item, idx) => (
            <div key={idx} className="flex flex-col">
               <div className="flex justify-between items-end mb-1 print:mb-0.5">
                  <span className="text-[11px] print:text-[9px] font-bold text-slate-700">{item.subject}</span>
                  <span className={`text-[10px] print:text-[8px] font-black ${item.status.color}`}>{item.A}/40 ({item.status.label})</span>
               </div>
               <div className="h-2 print:h-1.5 w-full bg-slate-100 rounded-full overflow-hidden print:border print:border-slate-200">
                  <div className={`h-full rounded-full ${item.status.bg.replace('100', '500')} print:bg-slate-500`} style={{ width: `${item.percent}%` }}></div>
               </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};


// --- 2. KURUMSAL (TOPLU) HESAPLAMA MOTORU ---
export const aggregateCokluZeka = (subs) => {
  const total = subs.length;
  if (total === 0) return null;

  const typeScores = {
      "Sözel-Dilsel Zeka": 0, "Mantıksal-Matematiksel Zeka": 0, "Görsel-Uzamsal Zeka": 0,
      "Müziksel-Ritmik Zeka": 0, "Bedensel-Kinestetik Zeka": 0, "Doğacı Zeka": 0,
      "Sosyal (Kişiler Arası) Zeka": 0, "İçsel (Öze Dönük) Zeka": 0
  };

  const classMap = {};

  // 1. ADIM: Tüm öğrencilerin tüm puanlarını topla
  subs.forEach(s => {
    const className = s.studentClass || s.sinif || "Tüm Sınıflar";
    if (!classMap[className]) {
      classMap[className] = { name: className, studentCount: 0, typeScores: { ...typeScores } };
    }
    classMap[className].studentCount++;

    const chartData = s.reportData?.chartData || [];
    
    if (chartData.length > 0) {
        chartData.forEach(item => {
            if (typeScores[item.subject] !== undefined) {
                typeScores[item.subject] += item.A;
                classMap[className].typeScores[item.subject] += item.A;
            }
        });
    }
  });

  // 2. ADIM: Puanları ortalamaya çevir ve BÜYÜKTEN KÜÇÜĞE sırala
  const avgScores = Object.entries(typeScores).map(([subject, totalScore]) => ({
      subject,
      avgScore: total > 0 ? Number((totalScore / total).toFixed(1)) : 0,
      percent: total > 0 ? Math.round((totalScore / (total * 40)) * 100) : 0 
  })).sort((a, b) => b.avgScore - a.avgScore);

  // 3. ADIM: İlk 3 sırayı doğrudan bu güvenilir "Ortalama" listesinden çek
  const topDominant = avgScores[0];
  const secondDominant = avgScores[1];
  const thirdDominant = avgScores[2];

  // 4. ADIM: Sınıf istatistiklerini de ortalamalara göre hesapla
  const classStats = Object.values(classMap).map(c => {
      let topType = "Belirsiz";
      let maxScore = -1;
      let maxPercent = 0;
      
      if (c.typeScores && c.studentCount > 0) {
          Object.entries(c.typeScores).forEach(([type, totalScore]) => {
              const avg = totalScore / c.studentCount;
              if (avg > maxScore) { 
                  maxScore = avg; 
                  topType = type; 
                  maxPercent = Math.round((avg / 40) * 100);
              }
          });
      }
      return { ...c, topType, percentInClass: maxPercent };
  }).sort((a, b) => b.studentCount - a.studentCount); 

  return { total, avgScores, topDominant, secondDominant, thirdDominant, classStats };
};


// --- 3. KURUMSAL RAPOR ARAYÜZÜ ---
export const CokluZekaKurumsalUI = ({ title, subs, aiResult, onGenerateAI, aiLoading, handlePrint, downloading }) => {
  const data = aggregateCokluZeka(subs);
  if (!data) return <div className="p-8 text-center text-slate-500">Bu gruba ait veri bulunamadı.</div>;

  const typeStyles = {
      "Sözel-Dilsel Zeka": { color: "text-blue-700", bg: "bg-blue-50", bar: "bg-blue-500", icon: BookOpen },
      "Mantıksal-Matematiksel Zeka": { color: "text-indigo-700", bg: "bg-indigo-50", bar: "bg-indigo-500", icon: BrainCircuit },
      "Görsel-Uzamsal Zeka": { color: "text-purple-700", bg: "bg-purple-50", bar: "bg-purple-500", icon: PieChartIcon },
      "Müziksel-Ritmik Zeka": { color: "text-rose-700", bg: "bg-rose-50", bar: "bg-rose-500", icon: Activity },
      "Bedensel-Kinestetik Zeka": { color: "text-orange-700", bg: "bg-orange-50", bar: "bg-orange-500", icon: Zap },
      "Doğacı Zeka": { color: "text-emerald-700", bg: "bg-emerald-50", bar: "bg-emerald-500", icon: Compass },
      "Sosyal (Kişiler Arası) Zeka": { color: "text-cyan-700", bg: "bg-cyan-50", bar: "bg-cyan-500", icon: Users },
      "İçsel (Öze Dönük) Zeka": { color: "text-slate-700", bg: "bg-slate-50", bar: "bg-slate-500", icon: Heart }
  };

  const TopIcon = typeStyles[data.topDominant.subject]?.icon || Star;
  const topColor = typeStyles[data.topDominant.subject]?.color || "text-slate-800";

  return (
    <>
      <div className="flex justify-end mb-4 no-print px-2">
         <button onClick={() => handlePrint('coklu-zeka-kurumsal-area')} disabled={downloading} className="bg-blue-600 text-white px-5 py-2.5 rounded-xl font-bold flex items-center text-sm shadow-md hover:bg-blue-700 transition">
            {downloading ? <Loader2 className="animate-spin h-5 w-5 mr-2"/> : <Download className="h-5 w-5 mr-2"/>} Raporu Yazdır / PDF
         </button>
      </div>

      <div id="coklu-zeka-kurumsal-area" className="font-sans flex flex-col bg-white print:bg-white">
        <div className="flex flex-col gap-5 print:gap-3 mb-6 print:mb-0">
          
          <div className="bg-slate-900 text-white p-5 print:p-3 rounded-2xl flex justify-between items-center shadow-sm shrink-0 avoid-break print:bg-white print:text-black print:border-b-2 print:border-slate-900">
            <div className="flex items-center gap-5 print:gap-3">
              <div className="h-14 w-14 print:h-10 print:w-10 rounded-xl flex items-center justify-center shadow-inner bg-indigo-600 print:bg-slate-800">
                <Brain className="h-7 w-7 print:h-5 print:w-5 text-white"/>
              </div>
              <div>
                <h2 className="text-2xl print:text-lg font-black leading-none mb-1">{title}</h2>
                <span className="text-xs print:text-[10px] text-slate-300 print:text-slate-600 font-bold tracking-widest uppercase block">ÇOKLU ZEKA KURUMSAL PROFİLİ</span>
              </div>
            </div>
            <div className="bg-white/10 px-5 py-2.5 rounded-xl text-center print:border print:border-slate-300">
              <span className="text-3xl print:text-xl font-black block text-indigo-300 print:text-indigo-700">{data.total}</span>
              <span className="text-[10px] print:text-[8px] block opacity-70 uppercase font-bold mt-1">Katılımcı</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 print:grid-cols-3 gap-5 print:gap-3 shrink-0 avoid-break">
            <div className={`p-5 print:p-3 rounded-2xl border shadow-sm flex flex-col justify-center ${typeStyles[data.topDominant.subject]?.bg} print:bg-white print:border-slate-300`}>
              <div className="flex items-center mb-1.5">
                 <TopIcon className={`h-6 w-6 print:h-4 print:w-4 mr-2 ${topColor}`}/>
                 <span className={`text-sm print:text-[10px] font-black uppercase tracking-widest ${topColor}`}>1. Baskın Zeka</span>
              </div>
              <p className={`text-2xl print:text-lg font-black mt-1 ${topColor}`}>{data.topDominant.subject} <span className="text-xs font-medium opacity-70">(%{data.topDominant.percent})</span></p>
            </div>
            <div className={`p-5 print:p-3 rounded-2xl border shadow-sm flex flex-col justify-center ${typeStyles[data.secondDominant.subject]?.bg || 'bg-slate-50'} print:bg-white print:border-slate-300`}>
              <span className={`text-sm print:text-[10px] font-black uppercase tracking-widest ${typeStyles[data.secondDominant.subject]?.color || 'text-slate-700'}`}>2. Baskın Zeka</span>
              <p className={`text-2xl print:text-lg font-black mt-1 ${typeStyles[data.secondDominant.subject]?.color || 'text-slate-800'}`}>{data.secondDominant.subject} <span className="text-xs font-medium opacity-70">(%{data.secondDominant.percent})</span></p>
            </div>
            <div className={`p-5 print:p-3 rounded-2xl border shadow-sm flex flex-col justify-center ${typeStyles[data.thirdDominant.subject]?.bg || 'bg-slate-50'} print:bg-white print:border-slate-300`}>
              <span className={`text-sm print:text-[10px] font-black uppercase tracking-widest ${typeStyles[data.thirdDominant.subject]?.color || 'text-slate-700'}`}>3. Destekleyici Zeka</span>
              <p className={`text-2xl print:text-lg font-black mt-1 ${typeStyles[data.thirdDominant.subject]?.color || 'text-slate-800'}`}>{data.thirdDominant.subject} <span className="text-xs font-medium opacity-70">(%{data.thirdDominant.percent})</span></p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-5 print:gap-3 shrink-0 avoid-break mt-1 print:mt-0">
            <div className="col-span-1 bg-blue-50 p-5 print:p-3.5 rounded-2xl border border-blue-100 flex flex-col justify-center shadow-sm print:bg-white print:border-slate-300">
               <h3 className="font-bold text-blue-900 mb-3 flex items-center text-xs uppercase tracking-widest border-b border-blue-200 pb-1.5"><Users className="h-5 w-5 mr-2 text-blue-600"/> Sınıfların Baskın Zekaları</h3>
               <div className="space-y-2 mt-1">
                 <div>
                    <span className="text-[10px] font-bold text-blue-500 uppercase tracking-widest block mb-0.5">Öne Çıkan Grup: {data.classStats[0] ? data.classStats[0].name : '-'}</span>
                    <p className="text-sm font-black text-slate-800 leading-tight">Zeka: {data.classStats[0] ? data.classStats[0].topType : 'Belirsiz'} <span className="text-[10px] font-bold text-indigo-600">(%{data.classStats[0]?.percentInClass})</span></p>
                 </div>
                 <div>
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-0.5">İkinci Grup: {data.classStats[1] ? data.classStats[1].name : '-'}</span>
                    <p className="text-xs font-black text-slate-700 leading-tight">Zeka: {data.classStats[1] ? data.classStats[1].topType : 'Belirsiz'} <span className="text-[10px] font-bold text-indigo-500">(%{data.classStats[1]?.percentInClass})</span></p>
                 </div>
               </div>
            </div>

            <div className="col-span-1 bg-purple-50 p-5 print:p-3.5 rounded-2xl border border-purple-100 flex flex-col justify-center shadow-sm print:bg-white print:border-slate-300">
               <h3 className="font-bold text-purple-900 mb-3 flex items-center text-xs uppercase tracking-widest border-b border-purple-200 pb-1.5"><BrainCircuit className="h-5 w-5 mr-2 text-purple-600"/> Kurumsal Öğrenme Stratejisi</h3>
               <p className="text-[11px] text-slate-700 leading-snug font-medium mt-1 text-justify">
                 Bu kurumdaki öğrencilerin öğrenme potansiyelini maksimize etmek için derslerde <strong>{data.topDominant.subject.split(' ')[0]}</strong> ve <strong>{data.secondDominant.subject.split(' ')[0]}</strong> zeka alanlarına hitap eden materyallere, projelere ve atölyelere ağırlık verilmelidir.
               </p>
            </div>

            <div className="col-span-2 bg-slate-50 p-5 print:p-3.5 rounded-2xl border border-slate-200 shadow-sm print:bg-white print:border-slate-300">
               <div className="flex justify-between items-center border-b border-slate-200 pb-2 mb-2">
                 <h3 className="font-bold text-slate-800 flex items-center text-xs uppercase tracking-widest"><BarChart3 className="h-5 w-5 mr-2 text-slate-600"/> Zeka Boyutları Ortalama Skor Dağılım</h3>
                 <span className="text-[10px] text-slate-500 italic">Max: 40 Puan</span>
               </div>
               <div className="space-y-3 print:space-y-2">
                  {data.avgScores.map((dim, idx) => (
                    <div key={idx}>
                      <div className="flex justify-between items-end mb-1 print:mb-0.5">
                        <span className="text-xs print:text-[9px] font-bold text-slate-700">{dim.subject}</span>
                        <span className="text-xs print:text-[9px] font-black text-slate-900">{dim.avgScore} <span className="text-[10px] text-slate-500 font-medium">Puan</span></span>
                      </div>
                      <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden print:border print:border-slate-200">
                        <div className={`h-full rounded-full ${typeStyles[dim.subject]?.bar || 'bg-slate-500'} print:bg-slate-600`} style={{ width: `${(dim.avgScore/40)*100}%` }}></div>
                      </div>
                    </div>
                  ))}
               </div>
            </div>
          </div>

          {/* YENİ EKLENEN: 2 VİZYONER STRATEJİ KARTI (Yan Yana ve Kompakt) */}
          {(() => {
              const learningFormats = {
                  "Sözel-Dilsel Zeka": "Okuma, Yazma ve Tartışma",
                  "Mantıksal-Matematiksel Zeka": "Problem Çözme ve Analiz",
                  "Görsel-Uzamsal Zeka": "Görsel Tasarım ve Şemalar",
                  "Müziksel-Ritmik Zeka": "İşitsel Destek ve Ritim",
                  "Bedensel-Kinestetik Zeka": "Fiziksel Deneyim ve Hareket",
                  "Doğacı Zeka": "Açık Alan Gözlemi ve Doğa",
                  "Sosyal (Kişiler Arası) Zeka": "Grup Etkileşimi",
                  "İçsel (Öze Dönük) Zeka": "Bireysel Çalışma"
              };

              // Zeka türüne göre gelişime açık alana özel öneriler
              const developmentSuggestions = {
                  "Sözel-Dilsel Zeka": "Sınıf içi tartışmalar, serbest okuma saatleri ve günlük yazma egzersizleri ile desteklenmelidir.",
                  "Mantıksal-Matematiksel Zeka": "Zeka oyunları, problem çözme senaryoları ve neden-sonuç analizleriyle geliştirilmelidir.",
                  "Görsel-Uzamsal Zeka": "Zihin haritaları, eğitici videolar ve kavramları resmetme etkinlikleriyle beslenmelidir.",
                  "Müziksel-Ritmik Zeka": "Konuları ritimle ezberleme ve arka plan müziği eşliğinde çalışma egzersizleri yapılmalıdır.",
                  "Bedensel-Kinestetik Zeka": "Sınıf içi hareketli oyunlar, rol yapma (drama) ve dokunarak öğrenme materyalleri artırılmalıdır.",
                  "Doğacı Zeka": "Açık hava etkinlikleri, doğa gözlem projeleri ve sınıf içi bitki/hayvan bakımı ile desteklenmelidir.",
                  "Sosyal (Kişiler Arası) Zeka": "Kubaşık öğrenme (grup çalışmaları), akran öğretimi ve takım oyunlarıyla güçlendirilmelidir.",
                  "İçsel (Öze Dönük) Zeka": "Bireysel çalışma zamanları, öz değerlendirme formları ve sessiz düşünme molaları eklenmelidir."
              };

              // Baskın zeka türüne göre ideal sınıf formatına özel aksiyonlar
              const formatSuggestions = {
                  "Sözel-Dilsel Zeka": "Münazaralar, hikayeleştirme ve etkileşimli soru-cevap seansları en yüksek verimi sağlar.",
                  "Mantıksal-Matematiksel Zeka": "Şifre çözme oyunları, deneyler ve mantık bulmacaları üzerinden işlenen dersler kalıcı olur.",
                  "Görsel-Uzamsal Zeka": "Renkli slaytlar, infografikler ve tahtada bol çizim kullanarak görselleştirilmiş anlatım yapılmalıdır.",
                  "Müziksel-Ritmik Zeka": "İşitsel materyaller (podcast vb.) ve melodik hatırlatıcılar öğrenme sürecini hızlandırır.",
                  "Bedensel-Kinestetik Zeka": "Deney düzenekleri kurma, ayağa kalkarak yanıt verme ve fiziksel materyal manipülasyonu şarttır.",
                  "Doğacı Zeka": "Mümkün olduğunca okul bahçesinde ders işleme ve doğal materyallerle sınıflandırma yapılmalıdır.",
                  "Sosyal (Kişiler Arası) Zeka": "Yuvarlak masa düzeni, proje grupları ve fikir alışverişine dayalı beyin fırtınaları merkeze alınmalıdır.",
                  "İçsel (Öze Dönük) Zeka": "Bağımsız araştırma ödevleri, kişisel hedefler belirleme ve yansıtıcı düşünme günlükleri etkili olur."
              };

              const lowestIntelligence = data.avgScores[data.avgScores.length - 1];
              const idealFormat = learningFormats[data.topDominant?.subject] || "Karma Eğitim Modeli";
              
              const devSuggestion = developmentSuggestions[lowestIntelligence?.subject] || "Farklı öğrenme stillerini kapsayan materyaller kullanılmalıdır.";
              const formatAction = formatSuggestions[data.topDominant?.subject] || "Tüm öğrencilere hitap edecek çeşitli bir öğretim tasarımı benimsenmelidir.";

              return (
                  <div className="grid grid-cols-2 gap-4 mt-2 print:mt-1 avoid-break">
                      {/* KART 1: Gelişime Açık Alan */}
                      <div className="bg-orange-50/50 rounded-2xl p-3 border border-orange-100 flex items-start print:bg-white print:border-slate-200">
                          <div className="bg-orange-100 p-2 rounded-lg mr-3 print:hidden shrink-0 mt-0.5">
                              <TrendingDown className="h-5 w-5 text-orange-600" />
                          </div>
                          <div className="flex-1">
                              <div className="flex justify-between items-start mb-0.5">
                                  <p className="text-[10px] font-bold text-orange-800/70 uppercase tracking-widest">GELİŞİME AÇIK ALAN</p>
                                  <span className="text-[9px] font-bold text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded print:hidden">% {lowestIntelligence?.percent}</span>
                              </div>
                              <h4 className="font-black text-slate-800 text-sm mb-1.5">{lowestIntelligence?.subject}</h4>
                              <div className="pt-1.5 border-t border-orange-200/60">
                                  <p className="text-[9.5px] text-orange-900/80 leading-snug font-medium text-justify">
                                      <strong className="text-orange-700">Öneri:</strong> {devSuggestion}
                                  </p>
                              </div>
                          </div>
                      </div>

                      {/* KART 2: İdeal Sınıf Formatı */}
                      <div className="bg-emerald-50/50 rounded-2xl p-3 border border-emerald-100 flex items-start print:bg-white print:border-slate-200">
                          <div className="bg-emerald-100 p-2 rounded-lg mr-3 print:hidden shrink-0 mt-0.5">
                              <Target className="h-5 w-5 text-emerald-600" />
                          </div>
                          <div className="flex-1">
                              <p className="text-[10px] font-bold text-emerald-800/70 uppercase tracking-widest mb-0.5">İDEAL SINIF FORMATI</p>
                              <h4 className="font-black text-slate-800 text-sm mb-1.5">{idealFormat}</h4>
                              <div className="pt-1.5 border-t border-emerald-200/60">
                                  <p className="text-[9.5px] text-emerald-900/80 leading-snug font-medium text-justify">
                                      <strong className="text-emerald-700">Aksiyon:</strong> {formatAction}
                                  </p>
                              </div>
                          </div>
                      </div>
                  </div>
              );
          })()}

        </div> {/* Flex Column Sonu */}

        <div className="hidden print:block" style={{ pageBreakBefore: 'always' }}></div>

        {/* AI RAPORU BÖLÜMÜ */}
        <div className="flex flex-col mt-4 print:mt-0">
          <div className="bg-purple-50 p-6 print:p-0 rounded-3xl border border-purple-100 shadow-sm flex flex-col pt-2 print:bg-white print:border-none print:shadow-none">
            <div className="flex justify-between items-center mb-4 border-b border-purple-200 pb-3 shrink-0 print:border-slate-300">
              <h3 className="font-bold text-purple-900 flex items-center text-base uppercase tracking-widest print:hidden"><Sparkles className="h-5 w-5 mr-2 text-purple-600"/> Yapay Zeka Strateji Raporu</h3>
              <h3 className="hidden print:flex items-center font-bold text-slate-900 text-sm uppercase tracking-widest w-full"><Brain className="h-5 w-5 mr-2"/> Kurumsal Öğrenme Stilleri ve Gelişim Planı</h3>
              <button onClick={() => onGenerateAI(title, data.avgScores)} disabled={aiLoading} className="px-5 py-2.5 bg-purple-600 text-white rounded-xl font-bold text-xs hover:bg-purple-700 transition shadow-sm print:hidden flex items-center">
                {aiLoading ? <Loader2 className="animate-spin h-4 w-4 mr-2"/> : <Brain className="h-4 w-4 mr-2"/>} {aiLoading ? "Analiz Ediliyor..." : "Raporu Oluştur"}
              </button>
            </div>
            {aiResult && (
            <div className="bg-white p-6 rounded-xl text-slate-800 border border-purple-100 print:border-0 print:p-0 print:text-black mt-4">
                <EditableAIBox text={aiResult} />
            </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};